import { useEffect, useRef } from "react";

const PAN_SPEED_PX_PER_SECOND = 600;

const PAN_KEYS: Record<string, { x: number; y: number }> = {
	ArrowUp: { x: 0, y: 1 },
	KeyW: { x: 0, y: 1 },
	ArrowDown: { x: 0, y: -1 },
	KeyS: { x: 0, y: -1 },
	ArrowLeft: { x: 1, y: 0 },
	KeyA: { x: 1, y: 0 },
	ArrowRight: { x: -1, y: 0 },
	KeyD: { x: -1, y: 0 },
};

interface Params {
	panByPixels: (dxPx: number, dyPx: number) => void;
}

/** Панорамирует чертёж стрелками или физическими клавишами WASD. */
export const useKeyboardPan = ({ panByPixels }: Params) => {
	const pressedKeys = useRef(new Set<string>());
	const frame = useRef<number | null>(null);
	const previousTime = useRef<number | null>(null);

	useEffect(() => {
		const stop = () => {
			if (frame.current !== null) cancelAnimationFrame(frame.current);
			frame.current = null;
			previousTime.current = null;
		};
		const tick = (time: number) => {
			const previous = previousTime.current ?? time;
			previousTime.current = time;
			let dx = 0;
			let dy = 0;
			for (const key of pressedKeys.current) {
				const direction = PAN_KEYS[key];
				if (!direction) continue;
				dx += direction.x;
				dy += direction.y;
			}
			if (dx || dy) {
				const length = Math.hypot(dx, dy);
				const distance = (Math.min(time - previous, 100) * PAN_SPEED_PX_PER_SECOND) / 1000;
				panByPixels((dx / length) * distance, (dy / length) * distance);
				frame.current = requestAnimationFrame(tick);
			} else {
				stop();
			}
		};
		const setKeyState = (event: KeyboardEvent, isPressed: boolean) => {
			const target = event.target;
			const isEditing =
				target instanceof HTMLInputElement ||
				target instanceof HTMLTextAreaElement ||
				target instanceof HTMLSelectElement ||
				(target instanceof HTMLElement && target.isContentEditable);
			if (!(event.code in PAN_KEYS) || isEditing) return;
			event.preventDefault();
			if (isPressed) pressedKeys.current.add(event.code);
			else pressedKeys.current.delete(event.code);
			if (isPressed && frame.current === null) frame.current = requestAnimationFrame(tick);
		};
		const onKeyDown = (event: KeyboardEvent) => setKeyState(event, true);
		const onKeyUp = (event: KeyboardEvent) => setKeyState(event, false);
		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("keyup", onKeyUp);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("keyup", onKeyUp);
			stop();
		};
	}, [panByPixels]);
};
